import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { CAPTCHA_SITE_KEY, IS_YANDEX_CAPTCHA, registerCaptchaReset } from '../../Libs/Captcha/captchaConfig'

// =====================================================================
// Görünmez captcha — Google reCAPTCHA veya Yandex SmartCaptcha.
//
// DIŞ ARAYÜZ `react-google-recaptcha` ile AYNI: ref üzerinden `executeAsync()`
// ve `reset()`. Kod tabanında ~20 çağrı yeri bu iki metodu kullanıyor; arayüzü
// koruyarak sağlayıcıyı değiştirmek o çağrı yerlerine hiç dokunmadan mümkün oldu.
//
// Sağlayıcı seçimi runtime-config.js'ten gelir (bkz. captchaConfig.js).
// Yapılandırma boşsa 'google' → TourZone davranışı değişmez.
// =====================================================================

const SDK_URL = 'https://smartcaptcha.yandexcloud.net/captcha.js'

/**
 * SmartCaptcha betiğini BİR KEZ yükler ve `window.smartCaptcha` hazır olunca çözer.
 *
 * Betik `?render=onload&onload=<fn>` ile çağrılıyor: aksi halde sayfadaki
 * `.smart-captcha` sınıflı elemanları KENDİ otomatik render ediyor ve bizim
 * kontrollü (görünmez, tek örnek) kurulumumuzla çakışıyor.
 */
let sdkPromise = null
const loadSmartCaptcha = () => {
    if (sdkPromise) return sdkPromise

    sdkPromise = new Promise((resolve, reject) => {
        if (window.smartCaptcha) {
            resolve(window.smartCaptcha)

            return
        }

        const cbName = '__tzSmartCaptchaOnload'
        window[cbName] = () => {
            delete window[cbName]
            resolve(window.smartCaptcha)
        }

        const s = document.createElement('script')
        s.src = `${SDK_URL}?render=onload&onload=${cbName}`
        s.async = true
        s.defer = true
        // Yükleme başarısızsa promise'i BAŞARISIZ bitir: aksi halde executeAsync
        // sonsuza kadar bekler ve giriş formu kilitlenir.
        s.onerror = () => {
            sdkPromise = null
            reject(new Error('SmartCaptcha betiği yüklenemedi'))
        }
        document.head.appendChild(s)
    })

    return sdkPromise
}

const YandexCaptcha = forwardRef(({ onLoaded, language }, ref) => {
    const boxRef = useRef(null)
    const widgetRef = useRef(null)
    // Bekleyen execute'un resolve/reject'i: token `callback` ile ASENKRON gelir.
    const pendingRef = useRef(null)
    const [ready, setReady] = useState(false)

    const settle = useCallback((token) => {
        const p = pendingRef.current
        pendingRef.current = null
        if (p) p.resolve(token || '')
    }, [])

    useEffect(() => {
        let cancelled = false
        let unsubscribe = []

        loadSmartCaptcha()
            .then((sdk) => {
                if (cancelled || !boxRef.current || widgetRef.current !== null) return

                widgetRef.current = sdk.render(boxRef.current, {
                    sitekey: CAPTCHA_SITE_KEY,
                    invisible: true,
                    hideShield: true,          // köşedeki rozet paneli kapatıyordu
                    hl: language || 'ru',
                    callback: settle,
                })

                // Kullanıcı bulmacayı kapatırsa ya da ağ/JS hatası olursa bekleyen
                // execute ASLA çözülmezdi → form sonsuza kadar "yükleniyor" kalırdı.
                const fail = (evt) => () => {
                    const p = pendingRef.current
                    pendingRef.current = null
                    if (p) p.reject(new Error(`SmartCaptcha: ${evt}`))
                }
                for (const evt of ['network-error', 'javascript-error']) {
                    unsubscribe.push(sdk.subscribe(widgetRef.current, evt, fail(evt)))
                }
                // Bulmaca kapandığında token gelmediyse iptal sayılır.
                unsubscribe.push(
                    sdk.subscribe(widgetRef.current, 'challenge-hidden', () => {
                        if (pendingRef.current) settle('')
                    })
                )

                setReady(true)
                if (onLoaded) onLoaded()
            })
            .catch(() => {
                // Betik yüklenemedi: uygulamanın açılmasını engelleme, sadece
                // "hazır" say ki ekran kilitlenmesin. executeAsync boş token döner
                // ve sunucu doğrulaması reddeder — güvenlik açılmaz.
                setReady(true)
                if (onLoaded) onLoaded()
            })

        return () => {
            cancelled = true
            for (const u of unsubscribe) { try { u() } catch (e) { /* yoksay */ } }
            try {
                if (widgetRef.current !== null && window.smartCaptcha) {
                    window.smartCaptcha.destroy(widgetRef.current)
                }
            } catch (e) { /* yoksay */ }
            widgetRef.current = null
        }
    }, [onLoaded, settle, language])

    const reset = useCallback(() => {
        try {
            if (widgetRef.current !== null && window.smartCaptcha) {
                window.smartCaptcha.reset(widgetRef.current)
            }
        } catch (e) { /* yoksay */ }
    }, [])

    useImperativeHandle(ref, () => ({
        reset,
        executeAsync: () => new Promise((resolve, reject) => {
            if (!ready || widgetRef.current === null || !window.smartCaptcha) {
                resolve('')                       // sunucu reddedecek; UI kilitlenmesin

                return
            }

            // Token TEK KULLANIMLIK. Çağrı yerlerinin bir kısmı reset etmiyor
            // (Google'da `window.grecaptcha.reset()` deniyorlar), o yüzden her
            // execute'tan ÖNCE burada sıfırlıyoruz — ikinci deneme bayat token
            // göndermesin.
            try { window.smartCaptcha.reset(widgetRef.current) } catch (e) { /* yoksay */ }

            pendingRef.current = { resolve, reject }
            try {
                window.smartCaptcha.execute(widgetRef.current)
            } catch (e) {
                pendingRef.current = null
                reject(e)
            }
        }),
    }), [ready, reset])

    // Görünmez modda da bir kap gerekiyor: bulmaca gerekirse SmartCaptcha
    // modalı buraya bağlıyor.
    return <div ref={boxRef} />
})

const CaptchaBox = forwardRef(({ onLoaded, language, badge = 'bottomright' }, ref) => {
    const innerRef = useRef(null)

    // Sağlayıcıdan bağımsız `resetCaptcha()` bu kaydı kullanır (bkz. captchaConfig).
    useEffect(() => {
        registerCaptchaReset(() => {
            if (innerRef.current && innerRef.current.reset) innerRef.current.reset()
        })

        return () => registerCaptchaReset(null)
    }, [])

    useImperativeHandle(ref, () => ({
        reset: () => innerRef.current && innerRef.current.reset && innerRef.current.reset(),
        executeAsync: () => (innerRef.current && innerRef.current.executeAsync
            ? innerRef.current.executeAsync()
            : Promise.resolve('')),
    }), [])

    if (IS_YANDEX_CAPTCHA) {
        return <YandexCaptcha ref={innerRef} onLoaded={onLoaded} language={language} />
    }

    return (
        <ReCAPTCHA
            ref={innerRef}
            badge={badge}
            size="invisible"
            sitekey={CAPTCHA_SITE_KEY}
            asyncScriptOnLoad={onLoaded}
        />
    )
})

export default CaptchaBox
